import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    input,
    linkedSignal,
    inject,
    computed,
} from "@angular/core";
import { Item } from "../../pojo/Item";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AvailCheck } from "../../pojo/AvailabilityCheck";
import { S25ObjectSearchUtil } from "../s25-object-search/s25.object.search.util";
import { UserStore } from "../../stores/user.store";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { TaskCompetingUtil } from "../s25-task/task.competing.util";
import { ObjectSearchAvailabilityUtil } from "../../util/object.search.availability.util";

export type Conflict = SpaceConflict | ResourceConflict;
export interface SpaceConflict extends AvailCheck.ConflictDefaults {}
export interface ResourceConflict extends AvailCheck.ConflictDefaults {}

@TypeManagerDecorator("s25-ng-conflicts")
@Component({
    selector: "s25-ng-conflicts",
    template: `
        @if (collapsible()) {
            <s25-simple-collapse
                [header]="'Conflict Details'"
                [expanded]="false"
                [attr.data-real]="isReal()"
                (expandedChange)="onCollapseChange($event)"
            >
                @if (onOpen() && !conflicts()) {
                    <s25-ng-loading-inline-static />
                } @else {
                    <ng-container [ngTemplateOutlet]="info" />
                }
            </s25-simple-collapse>
        } @else {
            <ng-container [ngTemplateOutlet]="info" />
        }

        <ng-template #info>
            @if (!!conflicts()) {
                <div class="cn-alert cn-alert--info cn-alert--{{ isReal() ? 'danger' : 'notRealConflict' }}">
                    <div
                        class="cn-alert__icon cn-icon {{ isReal() ? 'cn-alert__icon--red' : 'cn-alert__icon--blue' }}"
                        name="alert--info"
                    >
                        <s25-ng-icon [type]="'warningYield'" [ariaLabel]="false" />
                    </div>
                    <div class="cn-alert__label">
                        <div class="c-red ngBold c-margin-bottom--half">Conflicts on:</div>
                        @if (isRequestConflict()) {
                            <p class="request-conflict-intro c-margin-bottom--half">
                                @if (requestConflictLocation(); as location) {
                                    <s25-item
                                        [itemId]="location.itemId"
                                        [itemName]="location.itemName"
                                        [itemTypeId]="location.itemTypeId"
                                        [includeTypeIcon]="false"
                                    ></s25-item>
                                } @else {
                                    <span>{{ conflicts()?.[0]?.itemName ?? "This location" }}</span>
                                }
                                <span> has been requested on the dates below:</span>
                            </p>
                        }
                        @for (date of candidateDates(); track date.candidateKey) {
                            <div class="rose-form--conflictsRow c-margin-top--single">
                                @if (date.candidateName) {
                                    <p
                                        class="rose-form--conflictsSegment c-padding-bottom--quarter"
                                        [class.ngBold]="!date.candidateIsBound"
                                    >
                                        {{ date.candidateName }}
                                    </p>
                                }
                                <p class="rose-form--conflictsDate c-padding-bottom--quarter">
                                    {{ date.candidateDate | dateFormat: userStore.dateFormat() }}
                                </p>
                                @for (itemGroup of date.itemGroups; track itemGroup.itemKey) {
                                    @if (showLocationHeaders() && itemGroup.itemName && itemGroup.itemId) {
                                        <p class="rose-form--conflictsLocation c-padding-bottom--quarter">
                                            <s25-item
                                                [itemId]="itemGroup.itemId"
                                                [itemName]="itemGroup.itemName"
                                                [itemTypeId]="itemGroup.itemTypeId"
                                                [includeTypeIcon]="false"
                                            ></s25-item>
                                            @if (itemGroup.hasAlsoAssign) {
                                                <span> (Also Assign)</span>
                                            }
                                        </p>
                                    } @else if (showLocationHeaders() && itemGroup.itemName) {
                                        <p class="rose-form--conflictsLocation c-padding-bottom--quarter">
                                            {{ itemGroup.itemName
                                            }}{{ itemGroup.hasAlsoAssign ? " (Also Assign)" : "" }}
                                        </p>
                                    }
                                    <div>
                                        <ul>
                                            @for (conflict of itemGroup.conflicts; track conflict.uuid) {
                                                <li>
                                                    <div>
                                                        @switch (conflict.conflictType) {
                                                            @case ("blackout") {
                                                                <span>
                                                                    Blackout: {{ conflict.conflictName }}
                                                                    @if (conflict.conflictOverride?.blackout) {
                                                                        <span> (Overridden)</span>
                                                                    }
                                                                </span>
                                                            }
                                                            @case ("hours") {
                                                                <span>
                                                                    Closed: {{ conflict.conflictName }}
                                                                    @if (conflict.conflictOverride?.closed) {
                                                                        <span> (Overridden)</span>
                                                                    }
                                                                </span>
                                                            }
                                                            @case ("block") {
                                                                <span>
                                                                    Blocked:
                                                                    <s25-item
                                                                        [itemId]="conflict.conflictId"
                                                                        [itemName]="conflict.conflictName"
                                                                        [itemTypeId]="Item.Ids.Event"
                                                                    ></s25-item>
                                                                    @if (conflict.conflictOverride?.block) {
                                                                        <span> (Overridden)</span>
                                                                    }
                                                                </span>
                                                            }
                                                            @case ("rsrv") {
                                                                <span>
                                                                    <s25-item
                                                                        [itemId]="conflict.conflictId"
                                                                        [itemName]="conflict.conflictName"
                                                                        [itemTypeId]="Item.Ids.Event"
                                                                    ></s25-item>
                                                                    @if (
                                                                        conflict.conflictShare &&
                                                                        conflict.conflictOverride?.share
                                                                    ) {
                                                                        <span> (Shared)</span>
                                                                    }
                                                                </span>
                                                            }
                                                            @case ("pending") {
                                                                <span>
                                                                    <s25-item
                                                                        [itemId]="conflict.conflictId"
                                                                        [itemName]="conflict.conflictName"
                                                                        [itemTypeId]="Item.Ids.Event"
                                                                    ></s25-item>
                                                                </span>
                                                            }
                                                            @case ("req_rsrv") {
                                                                <span class="request-conflict-wrapper">
                                                                    @if (waitlistEnabled.value()) {
                                                                        <s25-ng-icon
                                                                            [type]="'queue'"
                                                                            [ariaLabel]="'Request Conflict'"
                                                                        />
                                                                    }
                                                                    {{ conflict.conflictName }}
                                                                </span>
                                                            }
                                                            @default {
                                                                <span>{{ conflict.conflictName }}</span>
                                                            }
                                                        }
                                                    </div>
                                                    @if (conflictSpan(conflict); as span) {
                                                        <span class="conflict-time-span">{{ span }}</span>
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </div>
            }
        </ng-template>
    `,
    styles: `
        ::ng-deep s25-ng-conflicts s25-simple-collapse .c-sectionHead {
            background: white !important;
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
        }
        ::ng-deep s25-ng-conflicts s25-simple-collapse .c-sectionHead::before {
            content: "";
            border-left: 0.25em solid var(--conflict-color);
        }

        ::ng-deep s25-ng-conflicts s25-simple-collapse[data-real="false"] .c-sectionHead::before {
            border-left: 0.25em solid var(--conflict-not-real-color);
        }

        ::ng-deep s25-ng-conflicts s25-simple-collapse .content-wrapper {
            font-size: 0.9em;
            padding: 1rem 0;
        }

        s25-simple-collapse .cn-alert {
            margin: 0;
        }

        ::ng-deep .request-conflict-wrapper {
            display: inline-flex;
            align-items: center;
            flex-wrap: nowrap;
        }

        .request-conflict-intro {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.25rem;
            margin: 0;
        }

        ::ng-deep .rose-form--conflictsRow s25-item .s25-item-link {
            color: var(--color--link-text, #2573a7);
            cursor: pointer;
        }

        .rose-form--conflictsRow li {
            margin-bottom: 11px;
        }

        .rose-form--conflictsRow li .conflict-time-span {
            display: block;
        }

        .rose-form--conflictsLocation {
            font-weight: bold;
            margin: 0.25rem 0 0.15rem;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.25rem;
        }

        .cn-alert {
            align-items: flex-start;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25ConflictsComponent {
    readonly model = input<Conflict[]>();
    readonly collapsible = input<boolean>(false);
    readonly onOpen = input<() => Promise<Conflict[]>>();

    readonly conflicts = linkedSignal(() => this.model());
    readonly isRequestConflict = computed(() => S25ObjectSearchUtil.hasOnlyRequestConflicts(this.conflicts()));
    readonly requestConflictLocation = computed(() => {
        const first = this.conflicts()?.[0];
        if (!first?.itemId || !first.itemName) return null;
        return {
            itemId: first.itemId,
            itemName: first.itemName,
            itemTypeId: first.itemTypeId ?? Item.Ids.Location,
        };
    });
    readonly showLocationHeaders = computed(() => {
        if (!this.isRequestConflict()) return true;
        // Request-conflict intro already names the location; only repeat headers when multiple differ
        const keys = new Set(
            (this.conflicts() ?? []).map(
                (conflict) => `${conflict.itemTypeId ?? ""}-${conflict.itemId ?? ""}-${conflict.itemName ?? ""}`,
            ),
        );
        return keys.size > 1;
    });
    readonly isReal = computed(() => {
        if (this.isRequestConflict()) return false;
        const conflicts = this.conflicts();
        return !conflicts ? undefined : conflicts.some((c) => c.isReal);
    });
    readonly candidateDates = computed(() => {
        const conflicts = this.conflicts();
        if (!conflicts?.length) return [];
        const byDate = S25Util.array.groupBy(
            conflicts,
            (conflict) => `${Number(conflict.candidateDate)}-${conflict.candidateName ?? ""}`,
        );
        return Object.values(byDate).map((dateConflicts: Conflict[]) => {
            const byItem = S25Util.array.groupBy(
                dateConflicts,
                (conflict) => `${conflict.itemTypeId ?? ""}-${conflict.itemId ?? ""}-${conflict.itemName ?? ""}`,
            );
            const itemGroups = Object.values(byItem).map((itemConflicts: Conflict[]) => {
                const first = itemConflicts[0]!;
                return {
                    itemKey: `${first.itemTypeId ?? ""}-${first.itemId ?? ""}-${first.itemName ?? ""}`,
                    itemId: first.itemId,
                    itemName: first.itemName,
                    itemTypeId: first.itemTypeId ?? Item.Ids.Location,
                    hasAlsoAssign: itemConflicts.some((conflict) => conflict.conflictRelation === "also"),
                    conflicts: itemConflicts,
                };
            });
            const firstConflict = dateConflicts[0]!;
            return {
                candidateKey: `${Number(firstConflict.candidateDate)}-${firstConflict.candidateName ?? ""}`,
                candidateDate: firstConflict.candidateDate,
                candidateName: firstConflict.candidateName,
                candidateIsBound: firstConflict.candidateIsBound,
                itemGroups,
            };
        });
    });

    readonly waitlistItemId = computed(() => {
        const first = this.model()?.[0];
        return first?.itemTypeId === Item.Ids.Location ? first.itemId : null;
    });

    readonly waitlistEnabled = s25Resource({
        if: () => this.waitlistItemId() !== null,
        params: () => this.waitlistItemId(),
        loader: ({ params: itemId, abortSignal }) =>
            !!itemId ? TaskCompetingUtil.waitlistEnabled(itemId, null, false) : Promise.resolve(false),
    });

    readonly userStore = inject(UserStore);
    protected readonly Item = Item;

    conflictSpan(conflict: Conflict): string {
        const start = conflict.conflictStart;
        const end = conflict.conflictEnd;
        // Error-message conflicts only include a calendar date (no reservation times).
        // The date is already shown as the section header — skip a misleading "12:00 AM - 12:00 AM".
        if (!start || !end || start.getTime() === end.getTime()) return "";

        return ObjectSearchAvailabilityUtil.getConflictSpan(conflict.conflictType, start, end, {
            dateTime: this.userStore.dateTimeFormat()!,
            time: this.userStore.timeFormat()!,
        });
    }

    async onCollapseChange(opened: boolean) {
        if (!opened) return;
        const onOpen = this.onOpen();
        if (!onOpen || !!this.conflicts()) return;
        this.conflicts.set(await onOpen());
    }
}
